import { Component, type ErrorInfo, type ReactNode } from 'react';
import { attemptAssetLoadRecovery } from '../../lib/assetLoadRecovery';
import { clearPersistedDashboardQueries } from '../../lib/queryPersister';
import {
  claimPageLoadRecovery,
  classifyPageLoadError,
  createPageLoadDiagnostic,
  flushPendingPageLoadDiagnostic,
  queuePageLoadDiagnostic,
  type PageLoadDiagnostic,
  type PageLoadFailureCategory,
} from '../../lib/pageLoadDiagnostics';

interface PageLoadErrorBoundaryProps {
  children: ReactNode;
  resetKey?: string;
  recoverInPlace?: boolean;
  onReload?: () => void;
  onFailure?: (failure: PageLoadDiagnostic) => void;
}

interface PageLoadErrorBoundaryState {
  hasError: boolean;
  category: PageLoadFailureCategory | null;
}

export class PageLoadErrorBoundary extends Component<
  PageLoadErrorBoundaryProps,
  PageLoadErrorBoundaryState
> {
  state: PageLoadErrorBoundaryState = {
    hasError: false,
    category: null,
  };

  static getDerivedStateFromError(error: Error): PageLoadErrorBoundaryState {
    return {
      hasError: true,
      category: error.name === 'DashboardDataContractError'
        ? 'data_contract'
        : classifyPageLoadError(error),
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const route = this.props.resetKey ?? window.location.pathname;
    const category = error.name === 'DashboardDataContractError'
      ? 'data_contract'
      : classifyPageLoadError(error);
    const diagnostic = createPageLoadDiagnostic({
      error,
      componentStack: errorInfo.componentStack ?? '',
      route,
      recoveryAttempt: 0,
      category,
    });
    queuePageLoadDiagnostic({
      error,
      componentStack: errorInfo.componentStack ?? '',
      route,
      recoveryAttempt: 0,
      category,
    });

    if (this.props.onFailure) {
      this.props.onFailure(diagnostic);
      return;
    }

    void flushPendingPageLoadDiagnostic();
    if (category === 'asset') {
      void attemptAssetLoadRecovery(diagnostic);
      return;
    }
    const isDashboard = diagnostic.route === '/dashboard';
    if (
      isDashboard
      && !this.props.recoverInPlace
      && claimPageLoadRecovery(diagnostic)
    ) {
      clearPersistedDashboardQueries();
      window.setTimeout(() => window.location.reload(), 0);
    }
  }

  componentDidUpdate(prevProps: PageLoadErrorBoundaryProps): void {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, category: null });
    }
  }

  private handleReload = (): void => {
    if (this.props.recoverInPlace && this.state.category !== 'asset') {
      this.setState({ hasError: false, category: null });
      return;
    }
    if (this.props.onReload) {
      this.props.onReload();
      return;
    }

    window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const route = (this.props.resetKey ?? window.location.pathname).split('?')[0];
    const isDashboard = route === '/dashboard';
    const isAssetFailure = this.state.category === 'asset';
    const title = isAssetFailure
      ? "We couldn't load this page"
      : isDashboard
        ? 'Dashboard ran into a problem'
        : 'This page ran into a problem';
    const description = isAssetFailure
      ? 'The page exists, but the app could not load the latest code. Reload the page to try again.'
      : this.props.recoverInPlace
        ? 'Try this page again. You can also use another page; sync progress remains available above.'
        : 'Reload the page to try again. If the problem continues, use another page and try again later.';

    return (
      <div className="flex min-h-[50vh] items-center justify-center px-4 py-16" role="alert">
        <div className="max-w-md text-center">
          <h1 className="text-2xl font-semibold text-foreground">{title}</h1>
          <p className="mt-3 text-sm leading-6 text-muted-foreground">
            {description}
          </p>
          <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <button
              type="button"
              className="inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
              onClick={this.handleReload}
            >
              {this.props.recoverInPlace && !isAssetFailure ? 'Try again' : 'Reload page'}
            </button>
            {!isDashboard && (
              <a
                href="/dashboard"
                className="inline-flex h-10 items-center justify-center rounded-md border border-input bg-background px-4 text-sm font-medium text-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
              >
                Go to dashboard
              </a>
            )}
          </div>
        </div>
      </div>
    );
  }
}
